import { Box, styled } from '@mui/material';
import React, { useRef, useMemo } from 'react';
import TabContent from '../../../atoms/tab/TabContent';
import HpTooltip from '../../../atoms/tooltip/HpTooltip';
import { ITabContentProps, SelectOptions } from '../../../commons/types';
import Title from '../../../atoms/title/Title';
import SubTitle from '../../../atoms/title/SubTitle';
import ResetButton from '../../../atoms/button/ResetButton';
import {
  MRT_Column,
  MRT_ColumnDef,
  MRT_TableInstance,
} from 'material-react-table';
import Button from '../../../atoms/button/Button';
import ImportIcon from '../../../assets/icons/Import.svg';
import { useParams } from 'react-router-dom';
import { useActions } from '../../../hooks/actions';
import {
  useGetClientStatementsQuery,
  useGetImpactModuleQuery,
} from '../../../store/api/leadership';
import ClientStatementLibraryTable from '../../../organisms/tables/Company/ClientStatementLibraryTable';
import { IGetClientStatementData } from '../../../store/api/types/leadership';
import OrganizationalImpactSLModal from '../../../organisms/modals/Statement/OrganizationalImpactSLModal';
import MultiSelectDropdown from '../../../atoms/multiselect/MultiSelectDropdown';
import _ from 'lodash';

const StyledTitlesWrapper = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
}));

const StyledTitleWrapper = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const StyledHeaderWrapper = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
}));

const StyledSubHeaderWrapper = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
}));

const StyledHeaderActionWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'end',
  alignItems: 'center',
  gap: '15px',
});

const ButtonIcon = styled('img')(() => ({
  width: '16px',
  height: '16px',
}));

export default function OrganizationalImpact({
  index,
  value,
}: ITabContentProps) {
  const tableInstanceRef = useRef<MRT_TableInstance>(null);

  const { id } = useParams();

  const { data: { data: organizationalImpact, metadata } = {}, isLoading } =
    useGetClientStatementsQuery({
      skillGroup: 'Organizational Impact',
      clientId: id || '',
    });

  const { openAddOrganizationalImpactSLModal, openImportStatementModal } =
    useActions();

  const onResetButtonClick = (): void => {
    tableInstanceRef.current !== null &&
      tableInstanceRef.current.resetColumnFilters();
  };

  const { data: dropdownData } = useGetImpactModuleQuery(id!);

  // multiSelect Onchange Event handler
  const multiSelectOnChangeHandler = (
    e: React.KeyboardEvent,
    newValue: SelectOptions[],
    reason: unknown,
    column: MRT_Column<IGetClientStatementData>
  ) => {
    // This is an onChange event to update the filter value for this column
    if (
      e.type === 'keydown' &&
      (e as React.KeyboardEvent).key === 'Backspace' &&
      reason === 'removeOption'
    ) {
      return;
    }

    const selectedItems = newValue.map((value: { value: string }) => {
      return value.value;
    });

    if (selectedItems.length > 0) {
      column.setFilterValue(
        (selectedItems.length > 0 && selectedItems) || undefined
      );
    } else {
      column.setFilterValue(undefined);
    }
  };

  const columns = useMemo<MRT_ColumnDef<IGetClientStatementData>[]>(
    () => [
      {
        header: 'Statement Name',
        accessorKey: 'name',
        filterFn: 'includesString',
      },
      {
        header: 'Statement ID',
        accessorKey: 'statementCode',
        filterFn: 'includesString',
      },
      {
        header: 'Description',
        accessorKey: 'description',
        filterFn: 'includesString',
      },
      {
        header: 'Impact Module',
        accessorKey: 'impactModuleName',
        filterVariant: 'multi-select',
        Filter: ({ column }) => (
          <MultiSelectDropdown
            dropDownItem={_.uniqBy(dropdownData?.dropdownData, 'value') ?? []}
            onChange={(e, newValue, reason) =>
              multiSelectOnChangeHandler(e, newValue, reason, column)
            }
            column={column}
          />
        ),
      },
    ],
    [dropdownData]
  );

  return (
    <TabContent
      index={index}
      value={value}
    >
      <>
        <OrganizationalImpactSLModal clientId={id} />
        <StyledTitlesWrapper>
          <StyledTitleWrapper>
            <StyledHeaderWrapper>
              <Title
                text={'Business Impact: Organizational Impact'}
                sx={{
                  fontSize: '28px',
                }}
              />
              <Box>
                <HpTooltip
                  title={
                    'Proven track record of professional accomplishments and ability to drive ' +
                    'business impact in common leadership domains related to leading people and the overall organization (role-agnostic)'
                  }
                />
              </Box>
            </StyledHeaderWrapper>
            <StyledHeaderActionWrapper>
              <Button
                btnType='SECONDARY'
                text={'Import data'}
                onClick={() => openImportStatementModal()}
                startIcon={
                  <ButtonIcon
                    src={ImportIcon}
                    alt='Import CSV'
                  />
                }
              />
              <Button
                btnType={'PRIMARY'}
                text={'Add new statement'}
                onClick={() => openAddOrganizationalImpactSLModal()}
              />
            </StyledHeaderActionWrapper>
          </StyledTitleWrapper>
          <StyledSubHeaderWrapper>
            <SubTitle
              text={`${
                metadata ? metadata.pagination.totalCount : '0'
              } total statements`}
            />
            <ResetButton
              onClick={onResetButtonClick}
              text='Reset'
            />
          </StyledSubHeaderWrapper>
        </StyledTitlesWrapper>

        <ClientStatementLibraryTable
          isLoading={isLoading}
          data={organizationalImpact || []}
          columns={columns}
          tableInstanceRef={tableInstanceRef}
        />
      </>
    </TabContent>
  );
}
